import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button, FormControl, IconButton, InputLabel, Modal, NativeSelect,
  Paper, useMediaQuery, useTheme,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import RefreshIcon from '@material-ui/icons/Refresh';
import NotificationsNoneIcon from '@material-ui/icons/NotificationsNone';
import { Skeleton } from '@material-ui/lab';
import { useStyles } from './DeliveryAlerts.styles';
import { MapControl } from '../MapControls/MapControls';
import { useMapControls } from '../MapControls/MapControlsContext';
import QueueContent from './QueueContent';

const EMPTY = [];
const LABELS = {
  invalid_phone: 'Invalid phone',
  alcohol_authorization: 'Alcohol authorization',
  submission_rejected: 'Submission rejected',
  validation_failed: 'Validation failed',
  submission_uncertain: 'Submission uncertain',
  delivery_cancelled: 'Delivery cancelled',
  delivery_return: 'Delivery return',
};

export const problemLabel = (issue) => {
  const category = typeof issue === 'string' ? issue : issue.category;
  return Object.prototype.hasOwnProperty.call(LABELS, category) ? LABELS[category] : category || 'Unavailable';
};

const compareId = (a, b) => {
  const left = String(a);
  const right = String(b);
  if (/^\d+$/.test(left) && /^\d+$/.test(right)) {
    return left.length - right.length || left.localeCompare(right);
  }
  return left.localeCompare(right);
};

export const sortIssues = (issues) => [...issues].sort((a, b) => (
  Number(b.severity === 'critical') - Number(a.severity === 'critical') ||
  (Date.parse(b.received_at) || 0) - (Date.parse(a.received_at) || 0) ||
  compareId(a.id, b.id)
));

export const mergeIssues = (previous, incoming) => {
  const records = new Map(previous.map((issue) => [String(issue.id), issue]));
  incoming.forEach((issue) => {
    const old = records.get(String(issue.id));
    if (!old || Number(issue.change_id) > Number(old.change_id)) {
      records.set(String(issue.id), issue);
    }
  });
  return sortIssues([...records.values()]);
};

const text = (value) => (
  value == null || value === '' ? 'Unavailable' :
    typeof value === 'object' ? 'Unavailable' : String(value)
);
const timestamp = (value) => {
  if (value == null || value === '') return 'Unavailable';
  const parsed = new Date(typeof value === 'number' && Math.abs(value) < 1e12 ? value * 1000 : value);
  return Number.isNaN(parsed.getTime()) ? text(value) : parsed.toLocaleString();
};

function Fields({ entries }) {
  const classes = useStyles();
  return (
    <dl className={classes.fields}>
      {entries.map(([label, value]) => (
        <React.Fragment key={label}><dt>{label}</dt><dd>{text(value)}</dd></React.Fragment>
      ))}
    </dl>
  );
}
Fields.propTypes = { entries: PropTypes.array.isRequired };

function Evidence({ issue }) {
  const classes = useStyles();
  return (
    <>
      <strong className={issue.severity === 'critical' ? classes.critical : undefined}>
        {problemLabel(issue)}
      </strong>
      <span className={classes.secondary}>
        {text(issue.severity)} · {text(issue.state)} · Issue {text(issue.id)}
      </span>
      <p className={classes.message}>{text(issue.message)}</p>
      <Fields entries={[
        ['Original attempt', issue.attempt_id],
        ['Provider', issue.provider],
        ['Provider reason', issue.provider_reason],
        ['External delivery ID', issue.external_delivery_id],
        ['Provider event', timestamp(issue.provider_event_at)],
        ['Received', timestamp(issue.received_at)],
        ['Updated', timestamp(issue.updated_at)],
        ['Seen', issue.acknowledged_at ? timestamp(issue.acknowledged_at) : 'Not seen'],
        ['Seen by', issue.acknowledged_by],
        ['Resolved', timestamp(issue.resolved_at)],
        ['Resolution', issue.resolution],
      ]} />
    </>
  );
}
Evidence.propTypes = { issue: PropTypes.object.isRequired };

function LoadingRows() {
  const classes = useStyles();
  return (
    <div className={classes.skeleton} aria-label="Loading alerts" role="status">
      {[0, 1, 2].map((key) => <Skeleton key={key} height={32} />)}
    </div>
  );
}

function OrderDetail({ issue, loadOrder, loadHistory }) {
  const classes = useStyles();
  const callbacks = useRef({ loadOrder, loadHistory });
  callbacks.current = { loadOrder, loadHistory };
  const alive = useRef(false);
  const historyBusy = useRef(false);
  const detailRoot = useRef(null);
  const [order, setOrder] = useState({ status: 'loading', data: null });
  const [history, setHistory] = useState({
    status: 'loading', results: [], cursor: undefined, more: false,
  });

  const fetchOrder = () => {
    setOrder({ status: 'loading', data: null });
    Promise.resolve().then(() => callbacks.current.loadOrder(issue)).then(
      (data) => { if (alive.current) setOrder({ status: data ? 'success' : 'error', data }); },
      () => { if (alive.current) setOrder({ status: 'error', data: null }); }
    );
  };
  const fetchHistory = (cursor) => {
    if (historyBusy.current) return;
    historyBusy.current = true;
    setHistory((old) => ({ ...old, status: 'loading' }));
    Promise.resolve().then(() => callbacks.current.loadHistory(issue, cursor)).then(
      (page) => {
        if (!page || !Array.isArray(page.results) ||
          (page.has_more && (page.next_cursor == null || page.next_cursor === cursor))) {
          throw new Error('Invalid issue page');
        }
        if (alive.current) {
          setHistory((old) => ({
            status: 'success',
            results: mergeIssues(old.results, page.results),
            cursor: page.next_cursor,
            more: Boolean(page.has_more),
          }));
        }
      }
    ).catch(() => {
      if (alive.current) setHistory((old) => ({ ...old, status: 'error' }));
    }).then(() => { historyBusy.current = false; });
  };

  useEffect(() => {
    alive.current = true;
    if (detailRoot.current) detailRoot.current.focus();
    fetchOrder();
    fetchHistory();
    return () => { alive.current = false; };
    // The parent keys this component by issue identity; requests run once per selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const data = order.data;
  return (
    <div className={classes.scroll} ref={detailRoot} tabIndex={-1} aria-label={`Order ${issue.order_id} alert details`}>
      <section className={classes.section} aria-label="Selected alert">
        <Evidence issue={issue} />
      </section>
      <section className={classes.section} aria-label="Order details">
        <h3 className={classes.sectionTitle}>Order details · read only</h3>
        {order.status === 'loading' && <LoadingRows />}
        {order.status === 'error' && (
          <div role="status">Order details unavailable. <Button size="small" onClick={fetchOrder}>Retry details</Button></div>
        )}
        {data && <Fields entries={[
          ['Order ID', data.orderId || data.id || issue.order_id],
          ['Status', data.status],
          ['Pickup', data.pickup && data.pickup.name],
          ['Pickup address', data.pickup && data.pickup.address],
          ['Pickup window', data.pickup && [data.pickup.start, data.pickup.end].filter(Boolean).map(timestamp).join(' – ')],
          ['Dropoff address', data.dropoff && data.dropoff.address],
          ['Dropoff window', data.dropoff && [data.dropoff.start, data.dropoff.end].filter(Boolean).map(timestamp).join(' – ')],
        ]} />}
      </section>
      <section className={classes.section} aria-label="Issue history">
        <h3 className={classes.sectionTitle}>Order issue history</h3>
        {history.results.map((entry) => (
          <article key={entry.id} className={classes.historyEntry}><Evidence issue={entry} /></article>
        ))}
        {history.status === 'loading' && <LoadingRows />}
        {history.status === 'error' && (
          <div role="status">History unavailable. <Button size="small" onClick={() => fetchHistory(history.cursor)}>Retry history</Button></div>
        )}
        {history.status === 'success' && !history.results.length && (
          <p className={classes.secondary}>{history.more ? 'No entries on this page. More history available.' : 'No history entries returned.'}</p>
        )}
        {history.more && history.status !== 'error' && (
          <div className={classes.more}>
            <Button size="small" disabled={history.status === 'loading'} onClick={() => fetchHistory(history.cursor)}>Load more</Button>
          </div>
        )}
      </section>
    </div>
  );
}
OrderDetail.propTypes = {
  issue: PropTypes.object.isRequired,
  loadOrder: PropTypes.func.isRequired,
  loadHistory: PropTypes.func.isRequired,
};

export default function DeliveryAlertsPanel({
  businesses = EMPTY, scopes = EMPTY, optionsStatus = 'loading',
  onRefreshScope, getRestaurantName, loadOrder, loadHistory, scheduleUnavailable = true, queue,
}) {
  const classes = useStyles();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const controls = useMapControls();
  const [localOpen, setLocalOpen] = useState(false);
  const open = controls ? controls.active === 'alerts' : localOpen;
  const setOpen = (value) => {
    if (controls) controls.setActive(value ? 'alerts' : null);
    else setLocalOpen(value);
  };
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [refreshError, setRefreshError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const launcher = useRef(null);
  const activeRow = useRef(null);
  const closeButton = useRef(null);
  const effectiveFilter = filter === 'all' || businesses.some((b) => String(b.id) === filter) ? filter : 'all';
  const scopeName = queue ? (queue.filters.business_id ?
    queue.businesses.find((b) => String(b.id) === String(queue.filters.business_id))?.name || `Business #${queue.filters.business_id}` :
    'All authorized businesses') : effectiveFilter === 'all' ? 'All authorized businesses' :
    businesses.find((b) => String(b.id) === effectiveFilter).name;
  const visibleScopes = scopes.filter((scope) => (
    businesses.some((business) => String(business.id) === String(scope.id)) &&
    (effectiveFilter === 'all' || String(scope.id) === effectiveFilter)
  ));
  const expected = businesses.filter((business) => effectiveFilter === 'all' || String(business.id) === effectiveFilter);
  const missing = expected.some((business) => !visibleScopes.some((scope) => String(scope.id) === String(business.id)));
  const incomplete = optionsStatus !== 'success' || missing ||
    visibleScopes.some((scope) => scope.status !== 'success' || scope.stale);
  // Production cannot establish scheduled-day eligibility from the released
  // contract. Keep raw monitor scopes intact; never present them as current.
  const issues = scheduleUnavailable ? [] : sortIssues(visibleScopes.reduce((all, scope) => (
    // Authorization failures must never leave cached rows visible.
    scope.status === 'forbidden' ? all : all.concat(scope.issues || [])
  ), []));
  const count = queue ? queue.data?.affected_order_count ?? '—' :
    scheduleUnavailable ? '—' : `${issues.length}${incomplete ? '+' : ''}`;
  const close = () => {
    setOpen(false);
    if (launcher.current) launcher.current.focus();
  };
  const goBack = () => {
    setSelected(null);
    requestAnimationFrame(() => { if (activeRow.current) activeRow.current.focus(); });
  };

  useEffect(() => {
    if (!open || controls) return undefined;
    if (closeButton.current) closeButton.current.focus();
    const escape = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        if (launcher.current) launcher.current.focus();
      }
    };
    document.addEventListener('keydown', escape);
    return () => document.removeEventListener('keydown', escape);
    // Shared controls own Escape and focus for the combined sheet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, Boolean(controls)]);

  // Drop detail data if the parent removes or forbids the selected business.
  const selectedAllowed = !scheduleUnavailable && selected && optionsStatus === 'success' &&
    businesses.some((business) => String(business.id) === String(selected.business_id)) &&
    scopes.some((scope) => String(scope.id) === String(selected.business_id) &&
      scope.status !== 'forbidden' && scope.status !== 'loading');
  useEffect(() => {
    if (selected && !selectedAllowed) setSelected(null);
  }, [selected, selectedAllowed]);

  const refresh = () => {
    if (queue) { queue.refresh(); return; }
    setSelected(null);
    setRefreshError(false);
    setRefreshing(true);
    Promise.resolve().then(() => onRefreshScope()).catch(() => setRefreshError(true)).then(() => setRefreshing(false));
  };
  const scopeStates = visibleScopes.filter((scope) => scope.status !== 'success' || scope.stale).map((scope) => {
    const business = businesses.find((b) => String(b.id) === String(scope.id));
    const status = scope.status === 'forbidden' ? 'Access unavailable' :
      scope.status === 'loading' ? 'Loading' :
        scope.status === 'error' ? 'Unavailable' : scope.stale ? 'Stale' : 'Not loaded';
    return `${business ? business.name : scope.id}: ${status}${scope.stale && status !== 'Stale' ? ' · stale' : ''}`;
  });

  const panel = (
    <Paper elevation={8} className={classes.paper} role={mobile && !controls ? 'dialog' : 'region'}
      aria-modal={mobile && !controls ? true : undefined} aria-label="Delivery alerts" tabIndex={-1}>
      <div className={classes.header}>
        {selected && selectedAllowed && <IconButton size="small" onClick={goBack} aria-label="Back to alerts"><ArrowBackIcon fontSize="small" /></IconButton>}
        <h2 className={classes.title}>{selected && selectedAllowed ? `Order #${selected.order_id}` : `Alerts · ${count}`}</h2>
        <IconButton size="small" onClick={refresh} disabled={refreshing} aria-label="Refresh alerts"><RefreshIcon fontSize="small" /></IconButton>
        <IconButton size="small" onClick={close} aria-label="Close alerts" ref={closeButton}><CloseIcon fontSize="small" /></IconButton>
      </div>
      {refreshError && <div className={classes.retry} role="status">Refresh failed. <Button size="small" onClick={refresh}>Retry</Button></div>}
      {queue && <QueueContent queue={queue} />}
      {!queue && scheduleUnavailable && <div className={classes.status} role="status">Scheduled delivery dates unavailable.</div>}
      {!queue && <div className={classes.listPane} style={selected && selectedAllowed ? { display: 'none' } : undefined}>
        <FormControl className={classes.filter}>
          <InputLabel htmlFor="delivery-alerts-business" shrink>Business</InputLabel>
          <NativeSelect value={effectiveFilter} onChange={(event) => setFilter(event.target.value)}
            inputProps={{ id: 'delivery-alerts-business', 'aria-label': 'Alert business filter' }}>
            <option value="all">All authorized businesses</option>
            {businesses.map((business) => <option key={business.id} value={String(business.id)}>{business.name}</option>)}
          </NativeSelect>
        </FormControl>
        {(incomplete || refreshing) && <div className={classes.status} role="status">
          {optionsStatus === 'loading' && <div>Loading businesses…</div>}
          {optionsStatus === 'error' && <div>Business list unavailable.</div>}
          {missing && <div>Some business scopes have not loaded.</div>}
          {scopeStates.map((status, index) => <div key={index}>{status}</div>)}
          {refreshing && <div>Refreshing…</div>}
           {incomplete && !scheduleUnavailable && <div>Count may be incomplete.</div>}
        </div>}
        <div className={classes.scroll}>
          <table className={classes.table} aria-label="Delivery alerts">
            <thead><tr>
              <th className={classes.th} style={{ width: '23%' }}>Order ID</th>
              <th className={classes.th} style={{ width: '33%' }}>Restaurant</th>
              <th className={classes.th}>Problem</th>
            </tr></thead>
            <tbody>{issues.map((issue) => (
              <tr key={`${issue.business_id}:${issue.id}`} onClick={(event) => {
                activeRow.current = event.currentTarget.querySelector('button');
                setSelected(issue);
              }} style={{ cursor: 'pointer' }}>
                <td className={classes.cell}>
                  <button className={classes.orderButton} onClick={(event) => {
                    activeRow.current = event.currentTarget;
                    setSelected(issue);
                  }} aria-label={`Open order ${issue.order_id}: ${problemLabel(issue)}`}>
                    #{issue.order_id}
                  </button>
                  <span className={classes.secondary}>{issue.acknowledged_at ? 'Seen' : 'Unseen'}</span>
                </td>
                <td className={classes.cell}>{getRestaurantName(issue) || 'Unavailable'}</td>
                <td className={classes.cell}>
                  <span className={issue.severity === 'critical' ? classes.critical : undefined}>{problemLabel(issue)}</span>
                  <span className={classes.secondary}>{text(issue.severity)}</span>
                </td>
              </tr>
            ))}</tbody>
          </table>
          {!scheduleUnavailable && !issues.length && (optionsStatus === 'loading' || visibleScopes.some((scope) => scope.status === 'loading')) ? <LoadingRows /> : null}
          {!scheduleUnavailable && !issues.length && <div className={classes.empty}>
            {incomplete ? 'No alerts available from loaded scopes.' : 'No open delivery alerts.'}
          </div>}
        </div>
      </div>}
      {selected && selectedAllowed && <OrderDetail key={`${selected.business_id}:${selected.id}`} issue={selected} loadOrder={loadOrder} loadHistory={loadHistory} />}
    </Paper>
  );

  const launchButton = (
      <Button className={controls ? undefined : classes.launcher} onClick={() => setOpen(!open)}
        ref={(element) => {
          launcher.current = element;
          if (controls) controls.launchers.current.alerts = element;
        }}
        aria-controls={controls ? 'map-control-alerts' : undefined}
        aria-expanded={open} aria-label={`Alerts ${queue ? count === '—' ? 'unavailable' : count : scheduleUnavailable ? 'unavailable' : count}, ${scopeName}${!queue && incomplete && !scheduleUnavailable ? ', incomplete' : ''}`}
        startIcon={<NotificationsNoneIcon fontSize="small" />}>
        <span className={classes.launcherText}>Alerts{!controls && <span className={classes.scopeCaption}>{scopeName}</span>}</span>
        <span className={classes.count}>{count}</span>
      </Button>
  );
  if (controls) return <MapControl id="alerts" launcher={launchButton}>{panel}</MapControl>;
  return (
    <>
      {launchButton}
      {mobile ? (
        <Modal open={open} onClose={close} keepMounted className={classes.mobileFrame}>{panel}</Modal>
      ) : (
        <div className={classes.desktopFrame} style={!open ? { display: 'none' } : undefined}>{panel}</div>
      )}
    </>
  );
}

DeliveryAlertsPanel.propTypes = {
  queue: PropTypes.object,
  scheduleUnavailable: PropTypes.bool,
  businesses: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.any.isRequired, name: PropTypes.string.isRequired })),
  scopes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.any.isRequired, status: PropTypes.string.isRequired,
    stale: PropTypes.bool, issues: PropTypes.array,
  })),
  optionsStatus: PropTypes.oneOf(['loading', 'success', 'error']),
  onRefreshScope: PropTypes.func,
  getRestaurantName: PropTypes.func,
  loadOrder: PropTypes.func,
  loadHistory: PropTypes.func,
};
